import { Feature, getFeatureSet } from "../phonemes/features";
import { Pattern } from "./pattern";

export function patternAsString(pattern: Pattern<string>): string | undefined {
  if (typeof pattern === 'string') {
    if (pattern === '') {
      return '∅';
    } else if (pattern[0] === ':') {
      return pattern === ':vowel' ? 'V' :
        pattern === ':consonant' ? 'C' :
          `[+${pattern.substring(1)}]`;
    } else if (pattern[0] === '/') {
      return pattern.substring(1, pattern.length - 1);
    } else if (pattern === '#word') {
      return '#';
    }
    return pattern;
  } else if ('or' in pattern) {
    const content = pattern.or.map(p => patternAsString(p));
    return `{${content.join('|')}}`;
  } else if ('and' in pattern) {
    const content = pattern.and.map(p => patternAsString(p));
    return `{${content.join('&')}}`;
  } else if ('not' in pattern) {
    const content = patternAsString(pattern.not);
    return `!${content}`;
  } else if ('seq' in pattern) {
    const content = pattern.seq.map(p => patternAsString(p));
    return `{${content.join(',')}}`;
  }
  // NYI
  return undefined;
}

export function stringAsPattern(s: string): Pattern<string> | PatternParsingError | null {
  if (s === undefined || s === '') {
    return null;
  } else if (s === '#') {
    return '#word';
  } else if (s === 'V') {
    return ':vowel';
  } else if (s === 'C') {
    return ':consonant';
  } else if (s[0] === '!') {
    const not = stringAsPattern(s.substring(1));
    if (Array.isArray(not)) {
      return not.length === 1 ? { not: `:${not[0]}` } : { patternError: 'unrecognized', context: s };
    }
    return isPatternParsingError(not) || !not ? not : { not };
  } else if (s[0] === '[') {
    const feature = s.replace(/\+|\]|\[/g, '');
    if (!getFeatureSet().has(feature as Feature)) {
      return { patternError: 'unrecognized feature', context: feature };
    }
    return `:${feature}`;
  } else if (s[0] === '{') {
    return chunk(s);
  } else {
    return `/${s}/`;
  }
}

export interface PatternParsingError {
  readonly patternError: 'missing }' | 'expected one of |&,' | 'unrecognized feature' | 'unrecognized';
  readonly context?: string;
}
export function isPatternParsingError(check: unknown): check is PatternParsingError {
  const test = check as PatternParsingError;
  return test && test.patternError !== undefined;
}
export function patternParsingErrorToString(error: PatternParsingError): string {
  return error.context ? `${error.patternError} in ${error.context}` : error.patternError;
}

/** break stuff in {} into chunks based on nested {} */
function chunk(s: string): Pattern<string> | PatternParsingError {
  const end = findMatchingBrace(s, 0);
  if (end === null) {
    return { patternError: 'missing }', context: s };
  }
  let curr = 1;
  const pieces: Pattern<string>[] = [];
  let divider: '|' | '&' | ',' | null = null;
  while (curr < end) {
    const c = s[curr];
    if (c === '{') {
      // grab {chunk}
      const chunkEnd = findMatchingBrace(s, curr);
      if (chunkEnd === null) {
        return { patternError: 'missing }', context: s };
      }
      const sub = s.substring(curr, chunkEnd + 1);
      const piece = chunk(sub);
      if (isPatternParsingError(piece)) {
        return piece; // error in nested piece
      }
      pieces.push(piece);
      curr = chunkEnd;
    } else if (c === '!' && s[curr + 1] === '{') { // NOT
      const inner = chunk(s.substring(curr + 1, end));
      if (isPatternParsingError(inner)) {
        return inner; // error in nested piece
      }
      pieces.push({ not: inner });
      curr = findMatchingBrace(s, curr + 1) as number;
    } else if (divider === null && pieces.length > 0) {
      // get the pattern's type
      if (c !== '|' && c !== '&' && c !== ',') {
        return { patternError: 'expected one of |&,', context: s };
      }
      divider = c;
    } else if (divider === null) {
      // first piece - look for first divider
      const result = findDivider(s, curr);
      if (result === null) {
        return { patternError: 'expected one of |&,', context: s };
      }
      divider = result[0];
      const piece = stringToPattern(s.substring(curr, result[1]));
      if (isPatternParsingError(piece)) {
        return piece;
      }
      pieces.push(piece);
      curr = result[1];
    } else if (c !== divider) {
      // a chunk not inside {} - look for next divider
      const check = s.indexOf(divider, curr);
      const next = check === -1 ? end : check;
      const sub = next === -1 ? s.substring(curr) : s.substring(curr, next);
      const piece = stringToPattern(sub);
      if (isPatternParsingError(piece)) {
        return piece;
      }
      pieces.push(piece);
      curr = next;
    }
    curr++;
  }

  if (pieces.length > 0) {
    switch (divider) {
      case '|': return { or: pieces };
      case '&': return { and: pieces };
      case ',': return { seq: pieces };
    }
  }
  return { patternError: 'unrecognized', context: s };
}

function stringToPattern(s: string): Pattern<string> | PatternParsingError {
  if (s === '∅') {
    return '';
  } else if (s === '#') {
    return '#word';
  } else if (s === 'V') {
    return ':vowel';
  } else if (s === 'C') {
    return ':consonant';
  } else if (s[0] === '[' && s[1] === '+') {
    const feature = s.substring(2, s.length - 1);
    if (!getFeatureSet().has(feature as Feature)) {
      return { patternError: 'unrecognized feature', context: feature };
    }
    return `:${feature}`;
  } else if (s[0] === '!') {
    const inner = stringToPattern(s.substring(1));
    if (isPatternParsingError(inner)) {
      return inner;
    }
    return { not: inner };
  }
  return `/${s}/`;
}

/** given that s[0] is {, find the matching }, skipping nested {} */
function findMatchingBrace(s: string, brace: number): number | null {
  let count = 1;
  for (let i = brace + 1; i < s.length; i++) {
    const c = s[i];
    if (c === '{') {
      count++;
    } else if (c === '}' && --count === 0) {
      return i;
    }
  }
  return null;  // missing }
}

function findDivider(s: string, start: number): ['|' | '&' | ',', number] | null {
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (c === '|' || c === '&' || c === ',') {
      return [c, i];
    }
  }
  return null;
}
